import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

export interface VoucherCategoryRole {
  id: number;
  name: string;
  guard_name?: string;
  pivot?: any;
}

export interface VoucherCategoryItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  roles_count?: number;
  vouchers_count?: number;
  roles?: VoucherCategoryRole[];
}

@Component({
  selector: 'app-manage-category-voucher-modal',
  templateUrl: './manage-category-voucher-modal.component.html'
})
export class ManageCategoryVoucherModalComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() outletId: any = null;

  @Output() close = new EventEmitter<void>();
  @Output() categoriesChanged = new EventEmitter<VoucherCategoryItem[]>();

  categories: VoucherCategoryItem[] = [];
  availableRoles: any[] = [];

  isLoading: boolean = false;
  isLoadingRoles: boolean = false;
  isSaving: boolean = false;
  isTogglingId: number | null = null;
  isDeletingId: number | null = null;

  searchTerm: string = '';
  viewMode: 'list' | 'form' = 'list';
  isEditMode: boolean = false;
  editingId: number | null = null;

  // Form Fields
  formName: string = '';
  formCode: string = '';
  formDescription: string = '';
  formColor: string = '#3B82F6';
  formSortOrder: number = 1;
  formIsActive: boolean = true;
  formRoleIds: number[] = [];

  // Preset colors
  presetColors: string[] = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#64748B'  // Slate
  ];

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    if (this.isOpen) {
      this.loadCategories();
      this.loadRoles();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.viewMode = 'list';
      this.loadCategories();
      this.loadRoles();
    }
  }

  get filteredCategories(): VoucherCategoryItem[] {
    if (!this.searchTerm?.trim()) {
      return this.categories;
    }
    const term = this.searchTerm.toLowerCase();
    return this.categories.filter(c =>
      c.name?.toLowerCase().includes(term) ||
      c.code?.toLowerCase().includes(term) ||
      (c.description && c.description.toLowerCase().includes(term))
    );
  }

  async loadCategories(): Promise<void> {
    this.isLoading = true;
    try {
      const outletParam = this.outletId ? `?outlet_id=${this.outletId}` : '';
      const response = await this.api.get(`api/v1/client/tenant/voucher-category/list${outletParam}`);
      if (response && (response.success || response.status === 'success' || response.status === true)) {
        const data = response.data;
        if (Array.isArray(data)) {
          this.categories = data;
        } else if (data && Array.isArray(data.data)) {
          this.categories = data.data;
        } else {
          this.categories = [];
        }
        this.categoriesChanged.emit(this.categories);
      }
    } catch (error) {
      console.error('Failed to load voucher categories:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadRoles(): Promise<void> {
    if (this.availableRoles.length > 0) return;
    this.isLoadingRoles = true;
    try {
      const response = await this.api.get('api/v1/client/tenant/role/list?page=1&limit=100');
      if (response && (response.success || response.status === 'success' || response.status === true)) {
        this.availableRoles = response.data?.list || response.data || [];
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      this.isLoadingRoles = false;
    }
  }

  openCreateForm(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.formName = '';
    this.formCode = '';
    this.formDescription = '';
    this.formColor = '#3B82F6';
    this.formSortOrder = (this.categories.length || 0) + 1;
    this.formIsActive = true;
    this.formRoleIds = [];
    this.viewMode = 'form';
  }

  async openEditForm(cat: VoucherCategoryItem): Promise<void> {
    this.isEditMode = true;
    this.editingId = cat.id;
    this.formName = cat.name;
    this.formCode = cat.code;
    this.formDescription = cat.description || '';
    this.formColor = cat.color || '#3B82F6';
    this.formSortOrder = cat.sort_order ?? 1;
    this.formIsActive = cat.is_active !== false;
    this.formRoleIds = (cat.roles || []).map(r => r.id);
    this.viewMode = 'form';

    // Fetch detail to ensure full roles information
    try {
      const response = await this.api.get(`api/v1/client/tenant/voucher-category/detail/${cat.id}`);
      if (response && (response.success || response.status === 'success' || response.status === true)) {
        const detail = response.data;
        if (detail) {
          this.formName = detail.name || this.formName;
          this.formCode = detail.code || this.formCode;
          this.formDescription = detail.description || '';
          this.formColor = detail.color || this.formColor;
          this.formSortOrder = detail.sort_order ?? this.formSortOrder;
          this.formIsActive = detail.is_active !== false;
          if (Array.isArray(detail.roles)) {
            this.formRoleIds = detail.roles.map((r: any) => r.id);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load detail category, using item data', error);
    }
  }

  onNameChange(): void {
    if (!this.isEditMode) {
      this.formCode = this.slugify(this.formName);
    }
  }

  slugify(text: string): string {
    return (text || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  selectColor(color: string): void {
    this.formColor = color;
  }

  toggleRole(roleId: number): void {
    const idx = this.formRoleIds.indexOf(roleId);
    if (idx > -1) {
      this.formRoleIds.splice(idx, 1);
    } else {
      this.formRoleIds.push(roleId);
    }
  }

  isRoleSelected(roleId: number): boolean {
    return this.formRoleIds.includes(roleId);
  }

  selectAllRoles(): void {
    this.formRoleIds = this.availableRoles.map(r => r.id);
  }

  clearAllRoles(): void {
    this.formRoleIds = [];
  }

  async saveCategory(): Promise<void> {
    if (!this.formName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validasi Gagal',
        text: 'Nama kategori wajib diisi.',
        confirmButtonColor: '#059669'
      });
      return;
    }

    this.isSaving = true;
    try {
      const payload: any = {
        name: this.formName.trim(),
        description: this.formDescription?.trim() || null,
        color: this.formColor || null,
        sort_order: Number(this.formSortOrder) || 1,
        is_active: this.formIsActive,
        role_ids: this.formRoleIds
      };

      let response: any;
      if (this.isEditMode && this.editingId) {
        response = await this.api.put(`api/v1/client/tenant/voucher-category/update/${this.editingId}`, payload);
      } else {
        payload.code = this.formCode.trim() || this.slugify(this.formName);
        response = await this.api.postAuto('api/v1/client/tenant/voucher-category/store', payload);
      }

      if (response && (response.success || response.status === 'success' || response.status === true)) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: response.message || (this.isEditMode ? 'Kategori berhasil diperbarui.' : 'Kategori berhasil dibuat.'),
          timer: 1500,
          showConfirmButton: false
        });
        this.viewMode = 'list';
        await this.loadCategories();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal',
          text: response?.message || 'Terjadi kesalahan saat menyimpan kategori.',
          confirmButtonColor: '#059669'
        });
      }
    } catch (error: any) {
      console.error('Failed to save category:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Terjadi kesalahan pada server.',
        confirmButtonColor: '#059669'
      });
    } finally {
      this.isSaving = false;
    }
  }

  async toggleActive(cat: VoucherCategoryItem): Promise<void> {
    this.isTogglingId = cat.id;
    const targetAction = cat.is_active ? 'disable' : 'enable';
    try {
      const response = await this.api.put(`api/v1/client/tenant/voucher-category/${targetAction}/${cat.id}`, {});
      if (response && (response.success || response.status === 'success' || response.status === true)) {
        cat.is_active = !cat.is_active;
        this.categoriesChanged.emit(this.categories);
        const toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });
        toast.fire({
          icon: 'success',
          title: response.message || `Kategori berhasil ${cat.is_active ? 'diaktifkan' : 'dinonaktifkan'}.`
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal',
          text: response?.message || 'Gagal mengubah status kategori.',
          confirmButtonColor: '#059669'
        });
      }
    } catch (error: any) {
      console.error('Failed to toggle status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Terjadi kesalahan.',
        confirmButtonColor: '#059669'
      });
    } finally {
      this.isTogglingId = null;
    }
  }

  async deleteCategory(cat: VoucherCategoryItem): Promise<void> {
    const confirm = await Swal.fire({
      title: 'Hapus Kategori?',
      html: `Kategori <b>${cat.name}</b> akan dihapus.<br/><span class="text-xs text-gray-500">Tindakan ini tidak dapat dibatalkan.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#6B7280'
    });

    if (!confirm.isConfirmed) return;

    this.isDeletingId = cat.id;
    try {
      const response = await this.api.delete('api/v1/client/tenant/voucher-category/delete', cat.id.toString());
      if (response && (response.success || response.status === 'success' || response.status === true)) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: response.message || 'Kategori voucher berhasil dihapus.',
          timer: 1500,
          showConfirmButton: false
        });
        await this.loadCategories();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Gagal',
          text: response?.message || 'Gagal menghapus kategori.',
          confirmButtonColor: '#059669'
        });
      }
    } catch (error: any) {
      console.error('Failed to delete category:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Terjadi kesalahan.',
        confirmButtonColor: '#059669'
      });
    } finally {
      this.isDeletingId = null;
    }
  }

  closeModal(): void {
    this.viewMode = 'list';
    this.close.emit();
  }
}
